/**
 * What it costs a unit to enter a tile, and whether it may at all.
 *
 * One place, because the pathfinder, the move command, the reachable-tiles overlay
 * and eventually the AI all have to agree. Two copies of a movement rule diverge.
 */

import type { Content, UnitDef } from '../../../content/schema/index.js';
import type { TileGrid } from '../state/types.js';

/** Returned as a cost when a tile cannot be entered at all. */
export const IMPASSABLE = Number.POSITIVE_INFINITY;

/**
 * Whether a unit of this type could ever stand on this tile, ignoring movement.
 *
 * `port` says the tile is one of the mover's own cities, which a ship may sail into.
 * The map alone cannot know that, so the caller says so.
 */
export function canEnter(content: Content, map: TileGrid, index: number, def: UnitDef, port = false): boolean {
  const terrain = content.terrain.get(map.terrain[index]!);
  if (!terrain.passable) return false;
  if (def.domain === 'land' && terrain.water) return false;
  if (def.domain === 'sea') {
    if (!terrain.water) return port;
    if (def.coastal && terrain.deep) return false;
  }
  return true;
}

/**
 * Move points needed to enter a tile, or IMPASSABLE.
 *
 * `ignore_terrain_cost` flattens every legal tile to one movement point — the classic
 * explorer's advantage, and what makes a scout worth building rather than a slower
 * warrior with better eyesight.
 */
export function moveCost(content: Content, map: TileGrid, index: number, def: UnitDef, port = false, from = -1): number {
  if (!canEnter(content, map, index, def, port)) return IMPASSABLE;

  const scale = content.rules.movement.scale;
  // Water is water to a ship, and a harbour is one more stretch of it.
  if (def.domain === 'sea') return scale;
  // Road to road — and every city stands on a road — is a fraction of a move (ADR-0055).
  if (from >= 0 && map.road[from] === 1 && map.road[index] === 1) return Math.min(scale, content.rules.movement.roadCost);
  if (def.abilities.includes('ignore_terrain_cost')) return scale;

  const terrain = content.terrain.get(map.terrain[index]!);
  const feature = content.features.get(map.feature[index]!);
  return (terrain.movementCost + feature.movementCost) * scale;
}

/** The cheapest any tile could cost. Used as the A* heuristic's scale factor. */
export function cheapestMoveCost(content: Content): number {
  return content.rules.movement.scale;
}

/**
 * Whether a unit with `movesLeft` may pay `cost` right now.
 *
 * The second clause is the rule that makes slow units usable: with any movement at
 * all left you may enter any legal tile, spending everything you have. Without it a
 * one-movement warrior could never set foot on a hill.
 */
export function canAfford(content: Content, movesLeft: number, cost: number): boolean {
  if (cost === IMPASSABLE) return false;
  if (movesLeft >= cost) return true;
  return content.rules.movement.allowSingleTileWhenDepleted && movesLeft > 0;
}

/** What is actually deducted — never more than the unit has. */
export function movesSpent(movesLeft: number, cost: number): number {
  return Math.min(cost, movesLeft);
}
